import { useCallback, useMemo, useState } from "react";

type ActionResult = { ok: true } | { ok: false; message?: string };

export type RetryBiometricFn = () => Promise<ActionResult>;

export interface UseBiometricErrorArgs {
    retryFn?: RetryBiometricFn;
    onRetrySuccess?: () => void;
    onEmailLogin?: () => void;
}

export interface UseBiometricErrorReturn {
    loading: boolean;
    error: string | null;
    retry: () => Promise<void>;
    goEmail: () => void;
    resetError: () => void;
}

/** Default retry navigates back to biometric-login which auto-starts the ceremony */
async function defaultRetry(): Promise<ActionResult> {
    return { ok: true };
}

export function useBiometricError(
    args: UseBiometricErrorArgs,
): UseBiometricErrorReturn {
    const { retryFn, onRetrySuccess, onEmailLogin } = args;

    const retryer = useMemo(() => retryFn ?? defaultRetry, [retryFn]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const resetError = useCallback(() => setError(null), []);

    const retry = useCallback(async () => {
        resetError();
        setLoading(true);

        try {
            const res = await retryer();
            if (!res.ok) {
                setError(res.message ?? "Retry failed. Please try again.");
                return;
            }
            onRetrySuccess?.();
        } catch (e) {
            setError(
                e instanceof Error
                    ? e.message
                    : "Retry failed. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    }, [onRetrySuccess, resetError, retryer]);

    const goEmail = useCallback(() => {
        if (loading) return;
        resetError();
        onEmailLogin?.();
    }, [loading, onEmailLogin, resetError]);

    return { loading, error, retry, goEmail, resetError };
}
